import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { MassMailState, MassMailAction } from '../types';
import { DEFAULT_SENDER_EMAIL } from '../utils/senderOptions';

// Initial state factory
const createInitialState = (overrides?: Partial<MassMailState>): MassMailState => ({
  // Resource and contact state
  selectedResources: [],
  contacts: [],
  isLoadingContacts: false,

  // Template state
  templateTypes: [],
  templates: [],
  contextTemplates: [],
  selectedType: "",
  selectedTemplate: "",
  filteredTemplates: [],

  // Email content state
  emailSubject: "",
  emailContent: "",
  // senderEmail: onderaan gezet, na de overrides (zie toelichting daar)
  emailSettings: null,

  // Parameters state
  parameters: [],
  parameterValues: {},

  // Context-specific placeholders
  contextPlaceholders: {},

  // File attachment state
  attachedFiles: [],
  pendingInvoiceAttachments: [],

  // UI state
  selectedPreviewContactId: "dummy",
  showAddEntity: false,
  mentionedEntities: [],
  showCreateParameter: false,
  newParameterName: '',
  newParameterType: 'global',

  // Results modal state
  isResultsModalOpen: false,
  sendResults: null,

  // Process state
  isSending: false,
  isRetrying: false,
  emailSendResults: null,

  // Excluded contacts (manually removed by user)
  excludedContactIds: new Set(),
  excludedContacts: [],

  // CC / BCC
  ccContacts: [],
  bccContacts: [],

  // Merge any overrides
  ...overrides,

  // Een aanroeper die een leeg verzendadres meegeeft (of het uit sessionStorage
  // terugleest zonder waarde) mag de dropdown niet leeg achterlaten: val terug
  // op het standaardadres.
  senderEmail: overrides?.senderEmail?.trim() || DEFAULT_SENDER_EMAIL,
});

// Reducer function
const massMailReducer = (state: MassMailState, action: MassMailAction): MassMailState => {
  switch (action.type) {
    case 'SET_SELECTED_RESOURCES':
      return { ...state, selectedResources: action.payload };
    case 'SET_CONTACTS':
      return { ...state, contacts: action.payload };
    case 'SET_LOADING_CONTACTS':
      return { ...state, isLoadingContacts: action.payload };
    case 'SET_TEMPLATE_TYPES':
      return { ...state, templateTypes: action.payload };
    case 'SET_TEMPLATES':
      return { ...state, templates: action.payload };
    case 'SET_CONTEXT_TEMPLATES':
      return { ...state, contextTemplates: action.payload };
    case 'SET_SELECTED_TYPE':
      return { ...state, selectedType: action.payload };
    case 'SET_SELECTED_TEMPLATE': {
      // Swap contacts when a template declares a recipient override. Selecting
      // an override-less template (or clearing the selection) restores the
      // baseline `defaultContacts` so users can still cycle between templates.
      const overrides = state.templateContactOverrides;
      const nextContacts = overrides && action.payload && overrides[action.payload]
        ? overrides[action.payload]
        : state.defaultContacts ?? state.contacts;
      return {
        ...state,
        selectedTemplate: action.payload,
        contacts: nextContacts,
      };
    }
    case 'SET_FILTERED_TEMPLATES':
      return { ...state, filteredTemplates: action.payload };
    case 'SET_EMAIL_SUBJECT':
      return { ...state, emailSubject: action.payload };
    case 'SET_EMAIL_CONTENT':
      return { ...state, emailContent: action.payload };
    case 'SET_SENDER_EMAIL':
      return { ...state, senderEmail: action.payload };
    case 'SET_EMAIL_SETTINGS':
      return { ...state, emailSettings: action.payload };
    case 'SET_PARAMETERS':
      return { ...state, parameters: action.payload };
    case 'SET_PARAMETER_VALUES':
      return { ...state, parameterValues: action.payload };
    case 'SET_CONTEXT_PLACEHOLDERS':
      return { ...state, contextPlaceholders: action.payload };
    case 'SET_ATTACHED_FILES':
      return { ...state, attachedFiles: action.payload };
    case 'SET_PENDING_INVOICE_ATTACHMENTS':
      return { ...state, pendingInvoiceAttachments: action.payload };
    case 'SET_SELECTED_PREVIEW_CONTACT_ID':
      return { ...state, selectedPreviewContactId: action.payload };
    case 'SET_SHOW_ADD_ENTITY':
      return { ...state, showAddEntity: action.payload };
    case 'SET_MENTIONED_ENTITIES':
      return { ...state, mentionedEntities: action.payload };
    case 'SET_SHOW_CREATE_PARAMETER':
      return { ...state, showCreateParameter: action.payload };
    case 'SET_NEW_PARAMETER_NAME':
      return { ...state, newParameterName: action.payload };
    case 'SET_NEW_PARAMETER_TYPE':
      return { ...state, newParameterType: action.payload };
    case 'SET_CC_CONTACTS':
      return { ...state, ccContacts: action.payload };
    case 'SET_BCC_CONTACTS':
      return { ...state, bccContacts: action.payload };
    case 'SET_IS_SENDING':
      return { ...state, isSending: action.payload };
    case 'SET_IS_RETRYING':
      return { ...state, isRetrying: action.payload };
    case 'SET_EMAIL_SEND_RESULTS':
      return { ...state, emailSendResults: action.payload };
    case 'SET_CONTACT_RECIPIENT_TYPE': {
      const { contactId, recipientType } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          if (contact.id === contactId) return { ...contact, recipientType };
          // Er kan maar één "Aan"-ontvanger zijn: de vorige zakt naar CC.
          if (recipientType === 'to' && contact.recipientType === 'to') {
            return { ...contact, recipientType: 'cc' as const };
          }
          return contact;
        }),
      };
    }
    case 'REMOVE_CONTACT':
      const contactToRemove = state.contacts.find(c => c.id === action.payload);
      return { 
        ...state, 
        contacts: state.contacts.filter(c => c.id !== action.payload),
        excludedContactIds: new Set([...state.excludedContactIds, action.payload]),
        excludedContacts: contactToRemove ? [...state.excludedContacts, contactToRemove] : state.excludedContacts
      };
    case 'ADD_EXCLUDED_CONTACT':
      return { 
        ...state, 
        excludedContactIds: new Set([...state.excludedContactIds, action.payload])
      };
    case 'RESTORE_CONTACT':
      const newExcludedContactIds = new Set(state.excludedContactIds);
      newExcludedContactIds.delete(action.payload.id);
      return {
        ...state,
        contacts: [...state.contacts, action.payload],
        excludedContactIds: newExcludedContactIds,
        excludedContacts: state.excludedContacts.filter(c => c.id !== action.payload.id)
      };
    case 'SET_RESULTS_MODAL_OPEN':
      return { ...state, isResultsModalOpen: action.payload };
    case 'SET_SEND_RESULTS':
      return { ...state, sendResults: action.payload };
    case 'RESET_STATE':
      return createInitialState();
    case 'RESET_FORM_DATA':
      return {
        ...state,
        // Clear user input data but keep loaded reference data
        selectedResources: [],
        contacts: [],
        isLoadingContacts: false,
        selectedType: "",
        selectedTemplate: "",
        filteredTemplates: state.templates, // Reset to all templates
        emailSubject: "",
        emailContent: "",
        parameters: [],
        parameterValues: {},
        contextPlaceholders: {},
        attachedFiles: [],
        pendingInvoiceAttachments: [],
        selectedPreviewContactId: "dummy",
        showAddEntity: false,
        mentionedEntities: [],
        showCreateParameter: false,
        newParameterName: '',
        newParameterType: 'global',
        excludedContactIds: new Set(),
        excludedContacts: [],
        ccContacts: [],
        bccContacts: [],
        isSending: false,
        isRetrying: false,
        emailSendResults: null,
        isResultsModalOpen: false,
        sendResults: null
      };
    default:
      return state;
  }
};

// Context type
interface MassMailContextType {
  state: MassMailState;
  dispatch: React.Dispatch<MassMailAction>;
}

// Create context
const MassMailContext = createContext<MassMailContextType | undefined>(undefined);

// Provider component
interface MassMailProviderProps {
  children: ReactNode;
  initialState?: Partial<MassMailState>;
}

export const MassMailProvider: React.FC<MassMailProviderProps> = ({ children, initialState: providedInitialState }) => {
  // Merge provided initial state with default initial state
  const mergedInitialState = createInitialState(providedInitialState);

  console.log('🔍 MassMailProvider initialization:', {
    providedInitialState,
    mergedInitialState: {
      emailSubject: mergedInitialState.emailSubject,
      emailContent: mergedInitialState.emailContent?.substring(0, 100),
      contactsCount: mergedInitialState.contacts.length,
      contextPlaceholders: mergedInitialState.contextPlaceholders
    }
  });

  const [state, dispatch] = useReducer(massMailReducer, mergedInitialState);

  return (
    <MassMailContext.Provider value={{ state, dispatch }}>
      {children}
    </MassMailContext.Provider>
  );
};

// Custom hook to use the context
export const useMassMailContext = () => {
  const context = useContext(MassMailContext);
  if (context === undefined) {
    throw new Error('useMassMailContext must be used within a MassMailProvider');
  }
  return context;
};
